/**
 * Option lists + the initial form state for the DSCR sizer.
 *
 * FIELD NAMES ARE THE CONTRACT. This form's state was the digital-sizer
 * prototype's snake_case (`fico`, `loan_amount`, `purpose`, `rent_override`,
 * `ppp`, `is_foreign_national`) posted straight to
 * `POST /pricing/scenario/dscr_quick`. That endpoint validates a
 * `DscrQuickPriceInput`, whose `loanPurpose` / `requestedLoanAmount` /
 * `asIsValue` are REQUIRED with no defaults — so every run 400'd
 * ("Invalid DSCR quick-price inputs"). The state below is now the model's own
 * vocabulary, which is also what makes a scenario savable as a loan without a
 * translation layer.
 *
 * The OPTION VALUES come from `sizersShared/sizerSelectOptions`, which
 * re-exports the portal's canonical `constants/selectOptions/*` lists — the
 * exact same objects the Loan Drilldown Details tab and the create-loan form
 * render. See that file for why the curated portal lists, not
 * `useModelFieldOptions`, are the source.
 */
import {
  selectOptionsLoanPurpose,
  selectOptionsPropertyType,
  dscrOptionsPrepaymentPenaltyStructure,
  selectOptionsCitizenshipStatus,
  dscrOptionsAmortizationType,
  sizerOptionsArmProduct,
  sizerOptionsVacancySelection,
  sizerOptionsTier,
  sizerOptionsYesNo,
} from "../sizersShared/sizerSelectOptions";
import { unitsForPropertyType } from "../../constants/selectOptions/unitsByPropertyType";
import {
  canBeDelayedPurchase,
  normalizeLegacyDelayedPurchase,
} from "../../utils/loans/delayedPurchase";

// loanPurpose — identical to the drilldown / create-loan list.
export const PURPOSES = selectOptionsLoanPurpose;

// propertyType — the full subjectProperty list, same as the property sidebar.
export const PROPERTY_TYPES = selectOptionsPropertyType;

// prepaymentPenaltyStructure — the drilldown's Loan Terms list MINUS the two
// flat schedules (5% x5 / 1% x5), which DSCR doesn't quote; see
// dscrOptionsPrepaymentPenaltyStructure.
// An UNSET value is meaningful here: the backend prices an omitted structure off
// the sheet's own "No Prepayment Penalty" row and max-price clamp, so this needs
// no "none" option and no client-side gate.
export const PPPS = dscrOptionsPrepaymentPenaltyStructure;

/**
 * amortizationType — the loan-model dropdown MINUS the bare "Interest Only"
 * basis, which the DSCR sizers do not offer at scenario stage (see
 * `dscrOptionsAmortizationType`). Values are verbatim, so a pick still matches
 * server-side by string equality; only the offered set narrows.
 *
 * It replaced the boolean `amortizing` chip group: `amortizing` is now DERIVED
 * server-side from this enum, so sending the enum is what actually selects the
 * qualifying-payment basis.
 */
export const AMORTIZATIONS = dscrOptionsAmortizationType;

// Pricing-engine vocabularies (no loan-model peer) — see sizerSelectOptions.
export const INT_TYPES = sizerOptionsArmProduct;
export const VACANCIES = sizerOptionsVacancySelection;
export const TIERS = sizerOptionsTier;

// citizenshipStatus — canonical borrower dropdown. It replaced an
// `is_foreign_national` boolean that exists on no model: the sheet's Foreign
// National row is reached through this field (and prices WITHOUT a FICO).
export const CITIZENSHIPS = selectOptionsCitizenshipStatus;

// Real booleans — the model's flags are bools, not the strings "true"/"false".
export const YES_NO = sizerOptionsYesNo;

/**
 * The form opens EMPTY — no product assumptions, no sample amounts. The
 * exceptions are the four inputs whose "unset" is not a real state: the
 * qualifying-payment basis, the ladder column, the vacancy basis and the pricing
 * counterparty all have a defined default on the input model, and echoing that
 * default is more honest than showing a blank that silently becomes it.
 */
export const INITIAL_FORM = {
  loanPurpose: "",
  // REQ-106: a boolean on DscrQuickPriceInput, asked only on a cash-out refi
  // (bought within 6 months, no debt — leveraged/documented as a purchase).
  delayedPurchase: null,
  // MINIMUM MARKET ENTRY IS A ZIP. The endpoint 422s without `address.zip` —
  // market data and the CFPB rural determination embed into the price — so the
  // Places picker writes the whole Address object and the ZIP box beside it is
  // the floor when no full address exists yet.
  address: {},
  // The rate the user wants priced. Sent, the Result block anchors to the
  // nearest ladder rung ("how THIS rate prices"); omitted, it anchors to best
  // execution. Stored as a DECIMAL fraction, like every other rate in the kit.
  requestedCoupon: "",
  requestedLoanAmount: "",
  asIsValue: "",
  purchasePrice: "",
  resolvedCreditScore: "",
  unitCount: 1,
  totalRents: "",
  totalMarketRents: "",
  vacancySelection: "Leased",
  recentRenovationOrConstruction: false,
  annualTaxes: "",
  annualInsurance: "",
  annualFloodInsurance: "",
  annualHoaFees: "",
  annualOtherExpense: "",
  amortizationType: "30-Year Fixed",
  armProduct: "fixed_30",
  prepaymentPenaltyStructure: "",
  citizenshipStatus: "US Citizen",
  crossCollateralized: false,
  // Sent as `propertyType` (DscrQuickPriceInput has the field): the backend
  // derives `unitCount` from it and sets the condo flags. The builder also
  // derives the two condo booleans so an older scenario reads the same.
  propertyType: "",
  // Routing extras (not DscrQuickPriceInput fields): at what tier, and whose
  // margin applies. No sheet: the server prices the platform sheet.
  correspondentId: null,
  tier: "BASE",
};

/**
 * Sample scenario behind the dev-only "Fill demo scenario" button. Every
 * selection is a value the field actually offers — a prefilled value is a posted
 * value, so it obeys the same rule as the option lists.
 */
export const DEMO_FORM = {
  ...INITIAL_FORM,
  loanPurpose: "Purchase",
  /* A REAL address, and NO coordinates.
   *
   * Coordinates are a geocode RESULT — the Places picker writes them, and
   * `cfpb_rural_check` trusts them enough to run the TIGER county-polygon
   * lookup directly off them instead of re-geocoding. Typing a plausible-looking
   * lat/lng here would hand the rural determination a number no geocoder
   * produced, so the demo seeds only what a person actually knows and lets the
   * backend derive FIPS from the address string (HouseCanary census, census.gov
   * geocoder fallback) — the same path a hand-typed address takes in production.
   *
   * Miami-Dade is not on the CFPB rural list, so the demo prices straight
   * through rather than landing on the rural gate. */
  address: {
    fullAddress: "1200 Brickell Ave, Miami, FL 33131, USA",
    streetNumber: "1200",
    streetName: "Brickell Avenue",
    city: "Miami",
    state: "FL",
    zip: "33131",
    county: "Miami-Dade County",
  },
  // 7.250% — a 12.5bps multiple, so it lands on a real ladder rung rather than
  // demonstrating the "snapped to nearest" path on every run.
  requestedCoupon: 0.0725,
  requestedLoanAmount: 500000,
  asIsValue: 700000,
  purchasePrice: 700000,
  resolvedCreditScore: 740,
  propertyType: "SFR",
  unitCount: 1,
  /* Rents sized so the demo actually EXERCISES the answer.
   *
   * At the old $4,000 this scenario carried a 0.94x DSCR: above the endpoint's
   * hard gate (0.80) but below BOTH of the sheet's DSCR pricing tiers, so the
   * Alternatives panel read "no passing rung" on every row and the one card the
   * redesign added showed nothing. At $5,200 the asked 7.250% lands ~1.22x — in
   * the 1.15+ tier — while the higher-coupon rungs fall through 1.15 into the
   * 1.00-1.15 tier, so both tier-bests populate and the outcome matrix spans a
   * passing/failing boundary instead of being uniformly one or the other. */
  totalRents: 5200,
  totalMarketRents: 5200,
  annualTaxes: 8000,
  annualInsurance: 2000,
  prepaymentPenaltyStructure: "5% 4% 3% 2% 1%",
};

/**
 * Form state → `DscrQuickPriceInput` + routing extras.
 *
 * The only transformation is UI-shape → model-shape: the property-type chip
 * becomes the two condo booleans the sheet's LLPA rows key off, and blank
 * numeric strings become numbers (or null, where the model's field is optional
 * and 0 would be a claim rather than an absence).
 */
const num = (v) => (v === "" || v == null ? null : Number(v));
const num0 = (v) => (v === "" || v == null ? 0 : Number(v));

export function buildDscrPayload(form, { rural } = {}) {
  const address = form.address || {};
  const hasAddress = Object.keys(address).some(
    (k) => address[k] !== "" && address[k] != null,
  );
  return {
    loanPurpose: form.loanPurpose,
    // Only meaningful on a cash-out refi; null anywhere else (the backend
    // clears it there too).
    delayedPurchase: canBeDelayedPurchase(form.loanPurpose)
      ? typeof form.delayedPurchase === "boolean"
        ? form.delayedPurchase
        : null
      : null,
    // The whole canonical Address object when the Places picker resolved one,
    // so the rural check runs at ADDRESS level (HC census / census.gov FIPS).
    // `zipCode` is the ZIP-only fallback the input model declares for when it
    // did not — the endpoint reads `address.zip` first, then this.
    address: hasAddress ? address : null,
    zipCode: address.zip || null,
    // Decimal fraction; the result anchors to the NEAREST ladder rung. Null
    // anchors it at best execution instead — a real choice, not a missing input.
    requestedCoupon: num(form.requestedCoupon),
    requestedLoanAmount: num0(form.requestedLoanAmount),
    asIsValue: num0(form.asIsValue),
    purchasePrice: num(form.purchasePrice),
    resolvedCreditScore: num(form.resolvedCreditScore),
    // The type DECIDES the units when it encodes a count (SFR = 1, "4-unit"
    // = 4 …) — the backend overrides a disagreeing unitCount anyway, and the
    // 2–4 / 5–9 unit pricing adjustments key off it. Only Multifamily /
    // Mixed-Use / Commercial take the entered number.
    propertyType: form.propertyType || null,
    unitCount:
      unitsForPropertyType(form.propertyType) ?? (num0(form.unitCount) || 1),
    totalRents: num0(form.totalRents),
    totalMarketRents: num0(form.totalMarketRents),
    vacancySelection: form.vacancySelection,
    // Exempts a VACANT refinance from the v8.0 −5% leverage reduction.
    recentRenovationOrConstruction: Boolean(form.recentRenovationOrConstruction),
    annualTaxes: num0(form.annualTaxes),
    annualInsurance: num0(form.annualInsurance),
    annualFloodInsurance: num0(form.annualFloodInsurance),
    annualHoaFees: num0(form.annualHoaFees),
    annualOtherExpense: num0(form.annualOtherExpense),
    amortizationType: form.amortizationType,
    armProduct: form.armProduct,
    // Omitted = the sheet's "No Prepayment Penalty" row, which is a real
    // pricing outcome — so an unset select posts null, not a guess.
    prepaymentPenaltyStructure: form.prepaymentPenaltyStructure || null,
    condo: form.propertyType === "Condo",
    nonWarrantableCondo: form.propertyType === "Non-Warrantable Condo",
    crossCollateralized: Boolean(form.crossCollateralized),
    citizenshipStatus: form.citizenshipStatus || null,
    correspondentId: form.correspondentId || undefined,
    tier: form.tier || "BASE",
    // RURAL GATE: a CFPB-rural property does not price until an exception is
    // REQUESTED. Never defaulted on — the flag is only ever set by the user
    // acting on the gate, and it rides onto the persisted scenario and the
    // priced result so the exception follows the loan.
    ...(rural?.requested
      ? {
          ruralExceptionRequested: true,
          ruralExceptionNote: rural.note || null,
        }
      : null),
  };
}

/**
 * The inverse — a stored scenario payload back into form state, for resuming a
 * persisted scenario (`?scenario=<id>`, from Priced Scenarios).
 *
 * Every field name already matches, so the only thing to undo is the one
 * UI-shape → model-shape transformation above: the two condo booleans become the
 * property-type selection again. Unset property type is the correct result for a
 * plain SFR — neither flag is true, and the picker's own default is blank.
 *
 * Missing keys fall back to `INITIAL_FORM` rather than `undefined`, so a payload
 * stored before a field existed still loads into a usable form.
 */
export function dscrPayloadToForm(stored) {
  if (!stored) return null;
  // A legacy "Delayed Purchase" purpose reads as cash-out + the flag (REQ-106).
  const scenario = normalizeLegacyDelayedPurchase(stored);
  // The stored type when the scenario sent one; older scenarios only carry
  // the condo flags, so fall back to reading those.
  const propertyType =
    scenario.propertyType ||
    (scenario.nonWarrantableCondo
      ? "Non-Warrantable Condo"
      : scenario.condo
        ? "Condo"
        : "");
  return {
    ...INITIAL_FORM,
    ...scenario,
    propertyType,
    // A scenario stored before the address gate existed carries neither key;
    // one stored ZIP-only carries `zipCode` and no address object.
    address:
      scenario.address || (scenario.zipCode ? { zip: scenario.zipCode } : {}),
    requestedCoupon: scenario.requestedCoupon ?? "",
    prepaymentPenaltyStructure: scenario.prepaymentPenaltyStructure || "",
    correspondentId: scenario.correspondentId ?? null,
  };
}

// No pricing-sheet picker and no `cpId` (pricing-scenario-sizers.md, owner
// 2026-10-08): the server prices the platform sheet — resfin-base, primary on
// EV and LQ; the portal's own base only while resfin-base has no active
// version — and ignores a cpId in the body. `result.cpId` names the sheet.
